import React, { useState, useCallback, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { numberToWords } from '../utils/formatCurrency';
import { settingsService, salesReceiptService, salesInvoiceService, customerService } from '../services/api';
import ReceiptPreview from '../components/ReceiptPreview';
import Footer from '../components/Footer';

const formatINR = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, days) => { const d = new Date(dateStr); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

const getFY = () => {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
  const s = m < 3 ? y - 1 : y; return `${String(s).slice(2)}-${String(s + 1).slice(2)}`;
};
const makeRctNo = (fy, num) => `RCT/${fy}/${String(num).padStart(6, '0')}`;

const defaultForm = () => ({
  receiptNumber: '',
  companyName: 'Neuberlinr Private Limited',
  companyAddress: 'No.47, Bharathi Street,\nTiruchirappalli - 620005,\nTamil Nadu',
  companyCIN: 'CIN: U70200TN2026PTC188018',
  companyGSTIN: 'GSTIN: 33AALCN1785B1ZL',
  companyPhone: '+91 73389 90669',
  companyEmail: 'madurai@eurocas.org',
  receivedFrom: '',
  customerId: null,
  receiptDate: today(),
  invoiceId: null,
  invoiceRef: '',
  paymentMethod: 'upi',
  referenceNumber: '',
  amount: '',
  notes: '',
});

const Receipts = () => {
  const [form, setForm] = useState(() => {
    try { const s = sessionStorage.getItem('receiptForm'); if (s) return { ...defaultForm(), ...JSON.parse(s) }; } catch {}
    return defaultForm();
  });
  const [editingFrom, setEditingFrom] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savedReceipts, setSavedReceipts] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const previewRef = useRef(null);
  const dlPreviewRef = useRef(null);
  const [dlReceipt, setDlReceipt] = useState(null);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const canDownload = form.receivedFrom.trim() && (parseFloat(form.amount) || 0) > 0;

  const loadReceipts = useCallback(async () => {
    try { const res = await salesReceiptService.list(); setSavedReceipts(res.data || []); } catch {}
  }, []);

  // Reset to a blank form with a fresh receipt number, discarding any leftover
  // state from a previously-viewed/edited receipt (e.g. switching to "New
  // Receipt" right after saving one, without clearing first) — otherwise the
  // form keeps the old receipt number and downloading silently updates that
  // receipt instead of creating a new one.
  const resetToNewReceipt = useCallback(async () => {
    sessionStorage.removeItem('receiptForm');
    const f = defaultForm();
    try {
      const fy = getFY();
      const val = await settingsService.get(`receipt_counter_${fy}`);
      f.receiptNumber = makeRctNo(fy, (parseInt(val || '0', 10)) + 1);
    } catch { f.receiptNumber = makeRctNo(getFY(), 1); }
    // Preselect the customer from the most recently created receipt —
    // receipts are often entered in a batch for the same customer (e.g.
    // successive installments), so this saves re-picking them each time.
    const last = savedReceipts[0];
    if (last?.customerId) {
      f.customerId = last.customerId;
      f.receivedFrom = last.formData?.receivedFrom || last.customerName || '';
    }
    setForm(f);
  }, [savedReceipts]);

  useEffect(() => {
    const init = async () => {
      if (!form.receiptNumber) {
        try {
          const fy = getFY();
          const val = await settingsService.get(`receipt_counter_${fy}`);
          setForm(f => ({ ...f, receiptNumber: makeRctNo(fy, (parseInt(val || '0', 10)) + 1) }));
        } catch { setForm(f => ({ ...f, receiptNumber: makeRctNo(getFY(), 1) })); }
      }
      loadReceipts();
      try { const res = await salesInvoiceService.list(); setInvoices(res.data || []); } catch {}
      try { const res = await customerService.list(); setCustomers(res.data || []); } catch {}
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { sessionStorage.setItem('receiptForm', JSON.stringify(form)); }, [form]);

  const generatePdfFromRef = useCallback(async (el, filename) => {
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: el.scrollWidth, height: el.scrollHeight });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfW = 210, pdfH = 297, imgW = pdfW, imgH = (canvas.height * pdfW) / canvas.width;
    if (imgH <= pdfH + 1) { pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH); }
    else { let yO = 0; while (yO < canvas.height) { const pc = document.createElement('canvas'); const sH = Math.min(canvas.height - yO, (canvas.width * pdfH) / pdfW); pc.width = canvas.width; pc.height = sH; const ctx = pc.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, sH); ctx.drawImage(canvas, 0, yO, canvas.width, sH, 0, 0, canvas.width, sH); if (yO > 0) pdf.addPage(); pdf.addImage(pc.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, imgW, (sH * pdfW) / canvas.width); yO += sH; } }
    pdf.save(filename);
  }, []);

  const saveReceipt = useCallback(async () => {
    if (!canDownload || !editingReceiptId) return;
    setSaving(true);
    try {
      const name = (form.receivedFrom || '').split('\n')[0].trim();
      await salesReceiptService.update(editingReceiptId, {
        receiptNumber: form.receiptNumber, customerId: form.customerId, customerName: name,
        receiptDate: form.receiptDate, invoiceId: form.invoiceId, paymentMethod: form.paymentMethod,
        referenceNumber: form.referenceNumber, amount: form.amount, notes: form.notes, formData: form,
      });
      await loadReceipts(); setEditingReceiptId(null); setShowPreview(false);
    } catch (e) { console.error('Failed to save:', e); } finally { setSaving(false); }
  }, [canDownload, editingReceiptId, form, loadReceipts]);

  const downloadPDF = useCallback(async () => {
    if (!canDownload || !previewRef.current) return;
    setGenerating(true);
    try { const fy = getFY(); const parts = form.receiptNumber.split('/'); await settingsService.set(`receipt_counter_${fy}`, String(parseInt(parts[parts.length - 1], 10) || 1)); } catch {}
    try {
      await generatePdfFromRef(previewRef.current, form.receiptNumber.replace(/\//g, '-') + '.pdf');
      try {
        const name = (form.receivedFrom || '').split('\n')[0].trim();
        const payload = { receiptNumber: form.receiptNumber, customerId: form.customerId, customerName: name, receiptDate: form.receiptDate, invoiceId: form.invoiceId, paymentMethod: form.paymentMethod, referenceNumber: form.referenceNumber, amount: form.amount, notes: form.notes, formData: form };
        const existing = savedReceipts.find(r => r.receiptNumber === form.receiptNumber);
        if (existing) await salesReceiptService.update(existing.id, payload);
        else await salesReceiptService.create(payload);
        await loadReceipts();
        if (editingReceiptId) { setEditingReceiptId(null); setShowPreview(false); }
      } catch (e) { console.error('Failed to save receipt:', e); }
    } catch (e) { console.error('PDF failed:', e); } finally { setGenerating(false); }
  }, [canDownload, form, savedReceipts, editingReceiptId, loadReceipts, generatePdfFromRef]);

  const downloadExisting = useCallback(async (rct) => {
    setDlReceipt(rct); await new Promise(r => setTimeout(r, 200));
    if (!dlPreviewRef.current) return;
    try { await generatePdfFromRef(dlPreviewRef.current, rct.receiptNumber.replace(/\//g, '-') + '.pdf'); } catch (e) { console.error('PDF failed:', e); } finally { setDlReceipt(null); }
  }, [generatePdfFromRef]);

  const showForm = activeTab === 'form' || (activeTab === 'list' && editingReceiptId);

  return (
    <>
      <div className="main-content">
        <div className="container">
          <div className="flex-between mb-lg">
            <h1>Receipts</h1>
            {activeTab === 'form' && !editingReceiptId && canDownload && (
              <button onClick={() => setShowPreview(p => !p)} className="btn btn-secondary">{showPreview ? 'Edit' : 'Preview'}</button>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--spacing-lg)', borderBottom: '2px solid var(--color-gray-200)' }}>
            <button onClick={() => { setActiveTab('list'); setShowPreview(false); setViewingReceipt(null); setEditingReceiptId(null); }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'list' && !editingReceiptId ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'list' && !editingReceiptId ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'list' && !editingReceiptId ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              Receipts {savedReceipts.length > 0 && <span style={{ background: 'var(--color-gray-200)', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)' }}>{savedReceipts.length}</span>}
            </button>
            <button onClick={() => {
              const formHoldsSavedReceipt = editingReceiptId || savedReceipts.some(r => r.receiptNumber === form.receiptNumber);
              if (formHoldsSavedReceipt) resetToNewReceipt();
              setActiveTab('form'); setViewingReceipt(null); setEditingReceiptId(null);
            }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'form' || editingReceiptId ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'form' || editingReceiptId ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'form' || editingReceiptId ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)' }}>
              {editingReceiptId ? 'Edit Receipt' : 'New Receipt'}
            </button>
          </div>

          {/* List */}
          {activeTab === 'list' && !viewingReceipt && !editingReceiptId && (
            savedReceipts.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">🧾</div><div className="empty-state-text">No receipts yet</div><button onClick={() => setActiveTab('form')} className="btn btn-primary mt-md">Create First Receipt</button></div>
            ) : (
              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                    {['Receipt No', 'Customer', 'Date', 'Invoice', 'Method'].map(h => <th key={h} style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>{h}</th>)}
                    <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Amount</th>
                    <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {savedReceipts.map(rct => (
                      <tr key={rct.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                        <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>{rct.receiptNumber}</td>
                        <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>{rct.customerName}</td>
                        <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{new Date(rct.receiptDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{rct.invoice?.invoiceNumber || '-'}</td>
                        <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', textTransform: 'capitalize' }}>{(rct.paymentMethod || '').replace('_', ' ')}</td>
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)' }}>{formatINR(rct.amount)}</td>
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setViewingReceipt(rct)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>View</button>
                            <button onClick={() => downloadExisting(rct)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Download</button>
                            <button onClick={() => { setForm({ ...defaultForm(), ...rct.formData, receiptNumber: rct.receiptNumber }); setViewingReceipt(null); setEditingReceiptId(rct.id); setShowPreview(false); }} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* View */}
          {activeTab === 'list' && viewingReceipt && !editingReceiptId && (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button onClick={() => setViewingReceipt(null)} className="btn btn-secondary btn-sm">Back to list</button>
                <button onClick={() => downloadExisting(viewingReceipt)} className="btn btn-primary btn-sm">Generate &amp; Download</button>
              </div>
              <div className="card mb-md" style={{ padding: 0, overflow: 'hidden' }}>
                <ReceiptPreview form={viewingReceipt.formData} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                <button onClick={() => setViewingReceipt(null)} className="btn btn-secondary">Back to list</button>
                <button onClick={() => downloadExisting(viewingReceipt)} className="btn btn-primary">Generate &amp; Download</button>
              </div>
            </>
          )}

          {activeTab === 'list' && editingReceiptId && (
            <div style={{ marginBottom: 'var(--spacing-md)' }}><button onClick={() => setEditingReceiptId(null)} className="btn btn-secondary btn-sm">Back to list</button></div>
          )}

          {/* Form */}
          {showForm && <>
            {showPreview && canDownload ? (
              <>
                <div className="card mb-md" style={{ padding: 0, overflow: 'hidden' }}><ReceiptPreview ref={previewRef} form={form} /></div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                  <button onClick={() => setShowPreview(false)} className="btn btn-secondary">Back to Edit</button>
                  <button onClick={downloadPDF} disabled={generating} className="btn btn-primary">{generating ? 'Generating...' : 'Generate & Download'}</button>
                </div>
              </>
            ) : (
              <>
                {/* From + Received From */}
                <div className="card mb-md">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="card-header">From</div>
                        <button onClick={() => setEditingFrom(e => !e)} className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}>{editingFrom ? 'Done' : 'Edit'}</button>
                      </div>
                      {editingFrom ? (
                        <>
                          <div className="form-group"><label className="form-label">Company Name</label><input className="form-input" value={form.companyName} onChange={e => set('companyName', e.target.value)} /></div>
                          <div className="form-group"><label className="form-label">Address</label><textarea className="form-input" style={{ minHeight: '60px', resize: 'vertical' }} value={form.companyAddress} onChange={e => set('companyAddress', e.target.value)} /></div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
                            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.companyPhone} onChange={e => set('companyPhone', e.target.value)} /></div>
                            <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Email</label><input className="form-input" value={form.companyEmail} onChange={e => set('companyEmail', e.target.value)} /></div>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                          <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-base)' }}>{form.companyName}</div>
                          {form.companyAddress && <div style={{ whiteSpace: 'pre-line' }}>{form.companyAddress}</div>}
                          {form.companyPhone && <div>Ph: {form.companyPhone}</div>}
                          {form.companyEmail && <div>Email: {form.companyEmail}</div>}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="card-header">Received From <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></div>
                      {customers.length > 0 && (
                        <div className="form-group">
                          {form.customerId ? (
                            <button type="button" onClick={() => set('customerId', null)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>
                              Change customer
                            </button>
                          ) : (
                            <select className="form-select" value="" onChange={e => {
                              const cid = e.target.value ? parseInt(e.target.value) : null;
                              const c = customers.find(x => x.id === cid);
                              const buildReceivedFrom = (cust) => {
                                const addr = (cust.address || '').trim();
                                const addrFirstLine = addr.split('\n')[0].trim();
                                // Some customer records already have the name baked into the
                                // address (bad past data entry) — don't repeat it in that case.
                                return addrFirstLine === cust.name.trim() ? addr : `${cust.name}\n${addr}`.trim();
                              };
                              setForm(f => ({ ...f, customerId: cid, receivedFrom: c ? buildReceivedFrom(c) : f.receivedFrom }));
                            }}>
                              <option value="">Select customer...</option>
                              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )}
                        </div>
                      )}
                      <textarea className="form-input" style={{ minHeight: '120px', resize: 'vertical', borderColor: !form.receivedFrom.trim() ? '#DC2626' : undefined, boxShadow: !form.receivedFrom.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} value={form.receivedFrom} onChange={e => set('receivedFrom', e.target.value)} placeholder={"Customer name\nAddress"} />
                    </div>
                  </div>
                </div>

                {/* Receipt Details */}
                <div className="card mb-md">
                  <div className="card-header">Receipt Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                    <div className="form-group">
                      <label className="form-label">Receipt Date</label>
                      <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                        <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('receiptDate', addDays(form.receiptDate, -1))}>-</button>
                        <input type="date" className="form-input" style={{ flex: 1 }} value={form.receiptDate} onChange={e => set('receiptDate', e.target.value)} />
                        <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('receiptDate', addDays(form.receiptDate, 1))}>+</button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Payment Method</label>
                      <select className="form-select" value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="upi">UPI</option>
                        <option value="cash">Cash</option>
                        <option value="cheque">Cheque</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Against Invoice</label>
                      <select className="form-select" value={form.invoiceId || ''} onChange={e => {
                        const iid = e.target.value ? parseInt(e.target.value) : null;
                        const inv = invoices.find(x => x.id === iid);
                        setForm(f => ({ ...f, invoiceId: iid, invoiceRef: inv ? inv.invoiceNumber : '', amount: inv ? String(inv.grandTotal) : f.amount }));
                      }}>
                        <option value="">None (standalone receipt)</option>
                        {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — {inv.customerName} — {formatINR(inv.grandTotal)}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Amount <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></label>
                      <input className="form-input no-spinner" type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)}
                        style={{ textAlign: 'right', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', borderColor: !(parseFloat(form.amount) > 0) ? '#DC2626' : undefined, boxShadow: !(parseFloat(form.amount) > 0) ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} />
                      {(parseFloat(form.amount) || 0) > 0 && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginTop: '2px', textAlign: 'right' }}>{numberToWords(form.amount)}</div>}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="card mb-md">
                  <div className="card-header">Notes</div>
                  <textarea className="form-input" style={{ minHeight: '50px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Payment details, remarks..." />
                </div>

                {/* Validation + Actions */}
                {!canDownload && (
                  <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <span style={{ fontSize: '16px' }}>!</span>
                    <span>
                      {!form.receivedFrom.trim() && <><strong>Received From</strong> is required. </>}
                      {!(parseFloat(form.amount) > 0) && <><strong>Amount</strong> is required.</>}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                  {editingReceiptId ? (
                    <>
                      <button onClick={() => setEditingReceiptId(null)} className="btn btn-secondary">Cancel</button>
                      <button onClick={saveReceipt} disabled={!canDownload || saving} className="btn btn-primary">{saving ? 'Saving...' : 'Save'}</button>
                    </>
                  ) : (
                    <>
                      <button onClick={resetToNewReceipt} className="btn btn-secondary">Clear</button>
                      <button onClick={() => setShowPreview(true)} disabled={!canDownload} className="btn btn-primary">Preview</button>
                    </>
                  )}
                </div>
              </>
            )}
          </>}
        </div>
      </div>

      {!showPreview && <div style={{ position: 'absolute', left: '-9999px', top: 0 }}><ReceiptPreview ref={previewRef} form={form} /></div>}
      {dlReceipt && <div style={{ position: 'absolute', left: '-9999px', top: 0 }}><ReceiptPreview ref={dlPreviewRef} form={dlReceipt.formData} /></div>}
      <Footer />
    </>
  );
};

export default Receipts;
